/**
 * EL INFORME DEL MENSUAL, REDISEÑADO (Enrique, 28-10).
 *
 * Lo que se abre en Seguimiento › Histórico con «Ver informe de <mes>». Ocho secciones, en
 * este orden y con estos nombres:
 *
 *   1 · Feedback del entrenador        4 · Lo que has hecho     7 · Tus fotos y tus medidas
 *   2 · Tu peso                        5 · Tu día tipo          8 · Lo que me has pedido para
 *   3 · Cómo te has sentido            6 · Tus alimentos            el mes que viene
 *
 * Todo lo calcula el servidor del día de inicio al día de reporte y lo manda en
 * `bloques.v2810` (`backend/core/informe_2810.py`); aquí solo se pinta. El feedback sigue
 * siendo el de siempre (`bloques.feedback`), con sus fotos, y solo sale cuando ya está escrito
 * (29-10): hasta entonces el informe empieza por «Tu peso».
 *
 * «No inventar texto»: lo que se lee es lo de los pantallazos del diseño. Sin dato, «No hay
 * dato disponible» (peso) o «No hay información disponible» (día tipo, alimentos).
 */
import React, { useMemo, useState } from 'react';
import { TusFotos } from './InformeDelMes';
import FotosDelFeedback from './FotosDelFeedback';
import { medidasEntre } from '../../lib/medidasEntreFotos';
import { num1 } from '../../lib/numeros';
import { ACTIVIDAD_VALOR } from '../../lib/respuestasDelCierre';

const ORANGE = '#FF671F';
const VERDE = '#4ADE80';
const SIN_DATO = 'No hay dato disponible';
const SIN_INFORMACION = 'No hay información disponible';
const REGISTROS_A_LA_VISTA = 10;

const Ceja = ({ children, className = '' }) => (
    <p className={`text-[11px] font-bold uppercase tracking-[0.12em] ${className}`} style={{ color: ORANGE }}>
        {children}
    </p>
);

const Seccion = ({ n, titulo, children, testid }) => (
    <section className="space-y-2" data-testid={testid}>
        <Ceja>{n} · {titulo}</Ceja>
        {children}
    </section>
);

const Caja = ({ children, className = '', style, ...resto }) => (
    <div className={`rounded-2xl bg-card border border-border p-4 ${className}`} style={style} {...resto}>{children}</div>
);

const Fila = ({ primera, children, className = '' }) => (
    <div className={`py-3 ${primera ? 'pt-0' : 'border-t border-border'} ${className}`}>{children}</div>
);

const SinInformacion = ({ texto = SIN_INFORMACION }) => (
    <p className="text-sm text-muted-foreground">{texto}</p>
);

// ── CABECERA ──────────────────────────────────────────────────────────────────────

const Cabecera = ({ c }) => (
    <div className="space-y-1.5 pb-4 border-b border-border">
        {c.ceja && <Ceja>{c.ceja}</Ceja>}
        <h2 className="text-3xl font-bold text-foreground uppercase leading-none"
            style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.02em' }}>
            Tu informe
        </h2>
        <p className="flex flex-wrap gap-x-5 gap-y-1 text-sm pt-1">
            {[['Objetivo', c.objetivo], ['Ciclo', c.ciclo], ['Check-in', c.checkin]]
                .filter(([, v]) => v).map(([que, v]) => (
                    <span key={que}>
                        <span className="text-muted-foreground">{que} · </span>
                        <span className="font-bold text-foreground">{v}</span>
                    </span>
                ))}
        </p>
    </div>
);

// ── 1 · FEEDBACK DE TU ENTRENADOR ───────────────────────────────────────────────────

const Feedback = ({ feedback, api, fotos }) => {
    if (feedback?.pendiente) {
        return <Caja><p className="text-sm text-muted-foreground">{feedback.aviso}</p></Caja>;
    }
    if (!feedback?.texto) return null;
    return (
        <Caja className="space-y-3" style={{ borderColor: ORANGE }}>
            <p className="text-[15px] text-foreground/90 leading-relaxed whitespace-pre-wrap break-words">{feedback.texto}</p>
            {api && <FotosDelFeedback api={api} fotos={fotos} />}
            {(feedback.firma || feedback.iniciales) && (
                <div className="flex items-center gap-2 pt-1">
                    {feedback.iniciales && (
                        <span className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold text-white"
                            style={{ backgroundColor: ORANGE }}>
                            {feedback.iniciales}
                        </span>
                    )}
                    <span className="text-[13px] text-muted-foreground">
                        {feedback.firma}{feedback.fecha_label ? ` · ${feedback.fecha_label}` : ''}
                    </span>
                </div>
            )}
        </Caja>
    );
};

// ── 2 · TU PESO ─────────────────────────────────────────────────────────────────────

const GraficaDePesos = ({ registros, linea }) => {
    const ps = registros;                       // del más antiguo al más nuevo
    const vs = ps.map(p => p.valor);
    const min = Math.min(...vs), max = Math.max(...vs);
    const rango = max - min || 1;
    const alto = (v) => 25 + ((v - min) / rango) * 75;          // en %: la más baja, un cuarto
    const indice = Object.fromEntries(ps.map((p, i) => [p.fecha, i]));
    // La línea: un punto por semana en el centro de su barra, cortada donde la semana no tiene.
    const tramos = [];
    let tramo = [];
    for (const s of linea || []) {
        if (s.valor == null || indice[s.fecha] == null) {
            if (tramo.length) tramos.push(tramo);
            tramo = [];
        } else {
            tramo.push([(indice[s.fecha] + 0.5) / ps.length * 100, 100 - alto(s.valor)]);
        }
    }
    if (tramo.length) tramos.push(tramo);
    return (
        <div className="relative h-32" data-testid="informe-pesos-grafica">
            <div className="absolute inset-0 flex items-end gap-[3px]">
                {ps.map(p => (
                    <div key={p.fecha} className={`flex-1 min-w-0 rounded-t-[3px] ${p.semanal ? '' : 'bg-foreground/15'}`}
                        style={{ height: `${alto(p.valor)}%`, ...(p.semanal ? { backgroundColor: ORANGE } : {}) }}
                        title={`${p.dia} · ${p.label} kg`} />
                ))}
            </div>
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none"
                aria-hidden="true">
                {tramos.map((t, i) => (t.length > 1 ? (
                    <polyline key={i} points={t.map(xy => xy.join(',')).join(' ')} fill="none" stroke="currentColor"
                        className="text-foreground/70" strokeWidth="1.5" vectorEffect="non-scaling-stroke"
                        strokeLinejoin="round" strokeLinecap="round" />
                ) : null))}
            </svg>
        </div>
    );
};

const PesosRegistrados = ({ p }) => {
    const [todos, setTodos] = useState(false);
    if (!p?.hay) return <Caja><SinInformacion texto={SIN_DATO} /></Caja>;
    const lista = [...p.registros].reverse();                  // del más nuevo al más antiguo
    const visibles = todos ? lista : lista.slice(0, REGISTROS_A_LA_VISTA);
    const quedan = lista.length - visibles.length;
    const primero = p.registros[0];
    const ultimo = p.registros[p.registros.length - 1];
    return (
        <Caja className="space-y-4" style={{ borderColor: ORANGE }} data-testid="informe-pesos-registrados">
            <div className="space-y-1 pb-3 border-b border-border">
                <Ceja>Desde el {p.desde_label} · {p.cuantos} {p.cuantos === 1 ? 'registro' : 'registros'}</Ceja>
                <h3 className="text-2xl font-bold text-foreground uppercase leading-none"
                    style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.02em' }}>Tu peso</h3>
            </div>
            <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-2">
                <GraficaDePesos registros={p.registros} linea={p.linea} />
                <div className="flex justify-between text-[11px] text-muted-foreground tabular-nums">
                    <span>{p.desde_label} · {primero.label} kg</span>
                    <span>{p.hasta_label} · {ultimo.label} kg</span>
                </div>
                <div className="flex flex-wrap gap-4 text-[11px] text-muted-foreground pt-1">
                    <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: ORANGE }} />Peso de la semana
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm bg-foreground/15" />Registro del día
                    </span>
                </div>
            </div>
            <div className="rounded-xl border border-border bg-muted/30 px-3">
                {visibles.map((r, i) => (
                    <div key={r.fecha} className={`flex items-center justify-between gap-3 py-2.5 ${i ? 'border-t border-border' : ''}`}>
                        <span className="flex items-center gap-2 min-w-0 text-sm text-foreground/80">
                            <span className="first-letter:uppercase truncate">{r.dia}</span>
                            {r.semanal && (
                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                                    style={{ color: ORANGE, backgroundColor: `${ORANGE}1F` }}>Semanal</span>
                            )}
                        </span>
                        <span className="text-sm font-bold text-foreground tabular-nums">
                            {r.label}{r === lista[0] ? ' kg' : ''}
                        </span>
                    </div>
                ))}
                {quedan > 0 && (
                    <button type="button" onClick={() => setTodos(true)} data-testid="informe-pesos-mas"
                        className="w-full text-left py-2.5 border-t border-border text-[13px] text-muted-foreground hover:text-foreground">
                        {quedan} {quedan === 1 ? 'registro más' : 'registros más'}, hasta el {p.desde_label}
                    </button>
                )}
            </div>
        </Caja>
    );
};

const TuPeso = ({ peso, registrados }) => {
    const [abierto, setAbierto] = useState(false);
    return (
        <>
            <Caja>
                {(peso?.filas || []).map((f, i) => (
                    <Fila key={f.clave} primera={i === 0} className="flex items-start justify-between gap-3">
                        <span className="min-w-0">
                            <span className="block text-[15px] text-foreground/90">{f.etiqueta}</span>
                            {f.pie && f.valor != null && <span className="block text-xs text-muted-foreground mt-0.5">{f.pie}</span>}
                        </span>
                        {f.valor == null
                            ? <span className="text-sm text-muted-foreground text-right">{SIN_DATO}</span>
                            : (
                                <span className="text-right whitespace-nowrap tabular-nums">
                                    <span className="text-base font-bold text-foreground">{f.valor}</span>
                                    {f.dif && <span className="text-xs text-muted-foreground ml-1.5">{f.dif}</span>}
                                </span>
                            )}
                    </Fila>
                ))}
                <button type="button" onClick={() => setAbierto(a => !a)} data-testid="informe-ver-pesos"
                    className="mt-1 pt-3 border-t border-border w-full text-left text-sm font-semibold" style={{ color: ORANGE }}>
                    Ver pesos registrados
                </button>
            </Caja>
            {abierto && <PesosRegistrados p={registrados} />}
        </>
    );
};

// ── 3 · CÓMO TE HAS SENTIDO ─────────────────────────────────────────────────────────

const CincoEstrellas = ({ n }) => (
    <span className="whitespace-nowrap text-lg leading-none" aria-label={`${n} de 5`}>
        {[1, 2, 3, 4, 5].map(i => (
            <span key={i} className={i <= n ? '' : 'text-foreground/25'} style={i <= n ? { color: ORANGE } : undefined}>★</span>
        ))}
    </span>
);

const ComoTeHasSentido = ({ s }) => (
    <Caja>
        <p className="text-sm text-foreground/80 pb-3">
            Sobre tus <span className="font-bold text-foreground">{s.cuantos} check-in</span> del mes.
        </p>
        {s.filas.map(f => (
            <Fila key={f.clave} className="flex items-center justify-between gap-3">
                <span className="min-w-0">
                    <span className="block text-[15px] text-foreground/90">{f.etiqueta}</span>
                    {f.pie && <span className="block text-xs text-muted-foreground mt-0.5">{f.pie}</span>}
                </span>
                <CincoEstrellas n={f.estrellas} />
            </Fila>
        ))}
    </Caja>
);

// ── 4 · LO QUE HAS HECHO ────────────────────────────────────────────────────────────

// Verde lo hecho, naranja lo hecho de otra forma, gris lo que faltó y con borde lo que no
// tocaba. Entrenos, Cardios y Actividad diaria (30-10) usan los mismos cuatro: el entreno
// adaptado y el tramo «Llegué al mínimo» van en naranja, el descanso y el día sin cardio
// con borde.
const COLOR_DEL_DIA = {
    si: { backgroundColor: VERDE }, cuadrada: { backgroundColor: VERDE },
    hecho: { backgroundColor: VERDE }, tal_cual: { backgroundColor: VERDE },
    master: { backgroundColor: VERDE }, creces: { backgroundColor: VERDE },
    sin_cuadrar: { backgroundColor: ORANGE }, adaptado: { backgroundColor: ORANGE },
    minimo: { backgroundColor: ORANGE },
};
const SIN_TOCAR = new Set(['sin_pauta', 'descanso', 'no_toca']);
const claseDelDia = (e) => (COLOR_DEL_DIA[e] ? '' : SIN_TOCAR.has(e)
    ? 'border border-dashed border-foreground/25' : 'bg-foreground/10 border border-foreground/10');

const Cuadros = ({ estados, testid }) => (
    <div className="flex flex-wrap gap-1.5 pt-2" data-testid={testid}>
        {estados.map((e, i) => (
            <span key={i} className={`w-4 h-4 rounded-[4px] ${claseDelDia(e)}`} style={COLOR_DEL_DIA[e]} />
        ))}
    </div>
);

// El tramo de actividad con el nombre del check-in, sin lo que va entre paréntesis.
const nombreDelTramo = (clave) => (ACTIVIDAD_VALOR[clave] || clave).split(' (')[0];

/** «Tal cual · 9  Adaptado · 3  …», como la de la dieta. */
const Leyenda = ({ leyenda }) => (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-2 text-xs text-muted-foreground">
        {(leyenda || []).map(l => (
            <span key={l.tono} className="flex items-center gap-1.5">
                <span className={`w-3 h-3 rounded-[3px] ${claseDelDia(l.tono)}`} style={COLOR_DEL_DIA[l.tono]} />
                {l.etiqueta || nombreDelTramo(l.tono)} · {l.dias}
            </span>
        ))}
    </div>
);

// `envuelve`: la de la actividad, cuyo tramo es largo, baja de línea en vez de partir la
// etiqueta de la fila (390 px).
const Cuenta = ({ hecho, resto, envuelve }) => (
    <span className={`text-sm tabular-nums ${envuelve ? 'text-right' : 'whitespace-nowrap'}`}>
        <span className="font-bold text-foreground">{hecho}</span>
        <span className="text-muted-foreground"> {resto}</span>
    </span>
);

const LoQueHasHecho = ({ h }) => (
    <Caja>
        <Fila primera>
            <div className="flex items-baseline justify-between gap-3">
                <span className="text-[15px] text-foreground/90">Check-in rellenados</span>
                <Cuenta hecho={h.checkin.hecho} resto={`de ${h.checkin.de}`} />
            </div>
            <Cuadros testid="informe-cuadros-checkin" estados={h.checkin.dias.map(d => (d ? 'si' : 'no'))} />
        </Fila>
        <Fila>
            <div className="flex items-baseline justify-between gap-3">
                <span className="text-[15px] text-foreground/90">Tu dieta, día a día</span>
                <Cuenta hecho={`${h.dieta.cuadradas} ${h.dieta.cuadradas === 1 ? 'cuadrada' : 'cuadradas'}`}
                    resto={`de ${h.dieta.creadas} ${h.dieta.creadas === 1 ? 'creada' : 'creadas'}`} />
            </div>
            <Cuadros testid="informe-cuadros-dieta" estados={h.dieta.dias} />
            <div className="flex flex-wrap gap-4 pt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-[3px]" style={{ backgroundColor: VERDE }} />Cuadrada · {h.dieta.cuadradas}</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-[3px]" style={{ backgroundColor: ORANGE }} />Sin cuadrar · {h.dieta.sin_cuadrar}</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-[3px] bg-foreground/10 border border-foreground/10" />Sin crear · {h.dieta.sin_crear}</span>
            </div>
        </Fila>
        {h.entrenos && (
            <Fila>
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[15px] text-foreground/90">Entrenos</span>
                    <Cuenta hecho={h.entrenos.hecho} resto={`de ${h.entrenos.de}`} />
                </div>
                <Cuadros testid="informe-cuadros-entrenos" estados={h.entrenos.tira} />
                <Leyenda leyenda={h.entrenos.leyenda} />
            </Fila>
        )}
        {h.cardio && (
            <Fila>
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[15px] text-foreground/90">Cardios</span>
                    <Cuenta hecho={h.cardio.hecho} resto={`de ${h.cardio.de}`} />
                </div>
                <Cuadros testid="informe-cuadros-cardio" estados={h.cardio.tira} />
            </Fila>
        )}
        {h.actividad && (
            <Fila>
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[15px] text-foreground/90 shrink-0">Actividad diaria</span>
                    <Cuenta envuelve hecho={nombreDelTramo(h.actividad.tramo)}
                        resto={`· ${h.actividad.dias_del_tramo} ${h.actividad.dias_del_tramo === 1 ? 'día' : 'días'}`} />
                </div>
                <Cuadros testid="informe-cuadros-actividad" estados={h.actividad.tira} />
                <Leyenda leyenda={h.actividad.leyenda} />
            </Fila>
        )}
        {h.suplementacion && (
            <Fila>
                <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[15px] text-foreground/90">Suplementación</span>
                    <Cuenta hecho={h.suplementacion.hecho} resto={`de ${h.suplementacion.de}`} />
                </div>
                <Cuadros testid="informe-cuadros-suplementacion" estados={h.suplementacion.dias} />
            </Fila>
        )}
    </Caja>
);

// ── 5 · TU DÍA TIPO ─────────────────────────────────────────────────────────────────

const TuDiaTipo = ({ d }) => (
    <Caja>
        <p className="text-sm text-foreground/80 pb-3">
            Tu combinación más repetida en cada comida, y las veces que la has hecho. Solo los alimentos, sin cantidades.
        </p>
        {!d?.hay ? <SinInformacion /> : d.filas.map(f => (
            <Fila key={f.clave} className="grid grid-cols-[5.5rem_1fr] gap-3">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground pt-0.5">{f.momento}</span>
                <span className="min-w-0">
                    <span className="block">
                        <span className="text-[15px] font-bold text-foreground">{f.nombre}</span>
                        {f.repetida
                            ? <span className="text-[11px] font-bold uppercase tracking-wider ml-1.5" style={{ color: ORANGE }}>· {f.veces} veces</span>
                            : <span className="text-[11px] font-semibold uppercase tracking-wider ml-1.5 text-muted-foreground">· Sin combinación repetida</span>}
                    </span>
                    {f.repetida && f.alimentos && (
                        <span className="block text-[13px] text-muted-foreground mt-0.5">{f.alimentos.join(' · ')}</span>
                    )}
                    {!f.repetida && f.ejemplo && (
                        <span className="block text-[13px] text-muted-foreground mt-0.5">
                            Ejemplo del único día que la hiciste: {f.ejemplo.map(a => a.toLowerCase()).join(' · ')}
                        </span>
                    )}
                </span>
            </Fila>
        ))}
    </Caja>
);

// ── 6 · TUS ALIMENTOS ───────────────────────────────────────────────────────────────

const TusAlimentos = ({ a }) => (
    <Caja>
        {!a?.hay ? <SinInformacion /> : (
            <>
                <p className="text-sm text-foreground/80 pb-3">
                    {a.cuantos === 1 ? 'Tu alimento más repetido' : `Tus ${a.cuantos} alimentos más repetidos`}, por categoría.
                    {' '}El número son las <span className="font-bold text-foreground">veces que lo has puesto en una comida</span>, no los gramos.
                </p>
                {a.grupos.map(g => (
                    <div key={g.clave} className="pt-2" data-testid={`informe-alimentos-${g.clave}`}>
                        <Ceja>{g.etiqueta}</Ceja>
                        {g.alimentos.map((x, i) => (
                            <div key={x.nombre} className={`flex items-baseline justify-between gap-3 py-2 ${i ? 'border-t border-border' : ''}`}>
                                <span className="text-sm text-foreground min-w-0 break-words">{x.nombre}</span>
                                <span className="text-sm font-bold tabular-nums" style={{ color: ORANGE }}>{x.veces}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </>
        )}
    </Caja>
);

// ── 7 · TUS FOTOS Y TUS MEDIDAS ─────────────────────────────────────────────────────

const _fecha = (dia) => {
    const d = new Date(`${dia}T12:00:00`);
    return isNaN(d) ? dia : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
};
// Sin dato, un guion normal: nunca rayas largas en lo que ve el cliente (revisor, 28-10).
const _cm = (v) => (v == null ? '-' : num1(v));
const _dif = (v) => (v == null ? '-' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${num1(Math.abs(v))}`);

const MedidasDeLasFotos = ({ tomas, par }) => {
    const [antes, despues] = par;
    const m = useMemo(() => medidasEntre(tomas, antes, despues), [tomas, antes, despues]);
    if (!antes || !despues) return null;
    return (
        <Caja data-testid="informe-medidas-fotos">
            {!m.hay ? <SinInformacion texto={SIN_DATO} /> : (
                <div className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-x-4 items-baseline text-sm">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground pb-2">Medidas</span>
                    <span className="text-xs text-muted-foreground text-right pb-2">{_fecha(antes)}</span>
                    <span className="text-xs text-muted-foreground text-right pb-2">{_fecha(despues)}</span>
                    <span className="text-xs text-muted-foreground text-right pb-2">Dif.</span>
                    {m.filas.map(f => (
                        <React.Fragment key={f.clave}>
                            <span className="border-t border-border py-2 text-foreground/90">{f.etiqueta}</span>
                            <span className="border-t border-border py-2 text-right tabular-nums text-foreground/80">{_cm(f.antes)}</span>
                            <span className="border-t border-border py-2 text-right tabular-nums font-bold text-foreground">{_cm(f.despues)}</span>
                            <span className="border-t border-border py-2 text-right tabular-nums text-muted-foreground">{_dif(f.dif)}</span>
                        </React.Fragment>
                    ))}
                    {(!m.tomaAntes || !m.tomaDespues) && (
                        <span className="col-span-4 pt-2 text-xs text-muted-foreground">
                            {SIN_DATO}: {_fecha(!m.tomaAntes ? antes : despues)}
                        </span>
                    )}
                </div>
            )}
            {m.hay && <p className="text-[11px] text-muted-foreground pt-2">En centímetros.</p>}
        </Caja>
    );
};

const FotosYMedidas = ({ api, token, tomas }) => {
    const [par, setPar] = useState([null, null]);
    const onPar = React.useCallback((a, b) => setPar(prev => (prev[0] === a && prev[1] === b ? prev : [a, b])), []);
    return (
        <div className="space-y-2">
            {api && <TusFotos api={api} token={token} tipo="mensual" sinTitulo onPar={onPar} />}
            <MedidasDeLasFotos tomas={tomas} par={par} />
        </div>
    );
};

// ── 8 · LO QUE ME HAS PEDIDO PARA EL MES QUE VIENE ─────────────────────────────────

const LoPedido = ({ p }) => (
    <Caja>
        {p.filas.map((f, i) => (
            <Fila key={f.clave} primera={i === 0}>
                <span className="block text-xs text-muted-foreground">{f.etiqueta}</span>
                <span className="block text-[15px] text-foreground mt-0.5 break-words">{f.valor}</span>
            </Fila>
        ))}
    </Caja>
);

// ── EL INFORME ──────────────────────────────────────────────────────────────────────

const InformeDelMes2810 = ({ informe, api, token, fotosDelFeedback }) => {
    const b = informe?.bloques || {};
    const v = b.v2810 || {};
    const secciones = [
        // EL BLOQUE DEL FEEDBACK, SOLO CUANDO LLEGA (Enrique, 29-10). Al enviar el mensual el
        // informe sale sin él -- antes iba aquí el aviso de «lo estamos revisando» --, y cuando
        // el entrenador lo escribe el informe se actualiza con este bloque, con sus fotos.
        ['feedback', 'Feedback del entrenador',
            b.feedback?.texto
                ? <Feedback feedback={b.feedback} api={api} fotos={fotosDelFeedback} /> : null],
        ['peso', 'Tu peso', <TuPeso peso={v.peso} registrados={v.pesos_registrados} />],
        ['sentido', 'Cómo te has sentido', v.sensaciones?.hay ? <ComoTeHasSentido s={v.sensaciones} /> : null],
        ['hecho', 'Lo que has hecho', v.hecho ? <LoQueHasHecho h={v.hecho} /> : null],
        ['dia-tipo', 'Tu día tipo', <TuDiaTipo d={v.dia_tipo} />],
        ['alimentos', 'Tus alimentos', <TusAlimentos a={v.alimentos} />],
        ['fotos', 'Tus fotos y tus medidas', <FotosYMedidas api={api} token={token} tomas={informe?.tomas_medidas} />],
        ['pedido', 'Lo que me has pedido para el mes que viene', v.pedido?.hay ? <LoPedido p={v.pedido} /> : null],
    ].filter(([, , contenido]) => contenido);

    return (
        <div className="space-y-6" data-testid="informe-del-mes-2810">
            {v.cabecera && <Cabecera c={v.cabecera} />}
            {secciones.map(([clave, titulo, contenido], i) => (
                <Seccion key={clave} n={i + 1} titulo={titulo} testid={`informe-seccion-${clave}`}>
                    {contenido}
                </Seccion>
            ))}
        </div>
    );
};

export default InformeDelMes2810;
